import { z } from 'zod';

const catalogId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const tariffRuleSchema = z.object({
  id: catalogId,
  name: z.string().trim().min(1).max(120),
  vehicleId: z.string().trim().min(1).max(64),
  startDate: isoDate,
  endDate: isoDate,
  multiplier: z.number().gt(0).max(10),
});

export const discountTierSchema = z.object({
  id: catalogId,
  minDays: z.number().int().min(1).max(365),
  percentOff: z.number().min(0).max(100),
});

export const extraSchema = z.object({
  id: catalogId,
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).default(''),
  pricePerDay: z.number().min(0).max(100000),
});

export const tariffCatalogSchema = z.object({
  rules: z.array(tariffRuleSchema).max(100).default([]),
  tiers: z.array(discountTierSchema).max(50).default([]),
  extras: z.array(extraSchema).max(50).default([]),
});

export const tariffPutSchema = z
  .object({
    rules: z.array(tariffRuleSchema).max(100).optional(),
    tiers: z.array(discountTierSchema).max(50).optional(),
    extras: z.array(extraSchema).max(50).optional(),
  })
  .superRefine((catalog, context) => {
    rejectInvertedDates(catalog.rules, context);
    rejectDuplicateIds('rules', catalog.rules, context);
    rejectDuplicateIds('tiers', catalog.tiers, context);
    rejectDuplicateIds('extras', catalog.extras, context);
  });

export type TariffCatalog = z.infer<typeof tariffCatalogSchema>;
export type TariffPut = z.infer<typeof tariffPutSchema>;

function rejectInvertedDates(
  rules: TariffPut['rules'],
  context: z.RefinementCtx,
): void {
  rules?.forEach((rule, index) => {
    if (rule.endDate < rule.startDate) {
      context.addIssue({
        code: 'custom',
        path: ['rules', index, 'endDate'],
        message: 'End date is before the start date.',
      });
    }
  });
}

function rejectDuplicateIds(
  key: 'rules' | 'tiers' | 'extras',
  rows: { id: string }[] | undefined,
  context: z.RefinementCtx,
): void {
  if (!rows) return;
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.id)) {
      context.addIssue({
        code: 'custom',
        path: [key],
        message: key === 'rules'
          ? 'Two season rules use the same id.'
          : key === 'tiers'
            ? 'Two discount tiers use the same id.'
            : 'Two extras use the same id.',
      });
      return;
    }
    seen.add(row.id);
  }
}

export function tariffPutError(error: z.ZodError): string {
  return error.issues[0]?.message ?? 'Invalid input';
}
